/**
 * PlannerSidebar — Buffer/holo.ai-inspired vertical nav for the Content
 * Planner module. Persistent across every /portal/content-planner,
 * /portal/workspace, and /portal/content-studio route.
 *
 *   • Top: HidroVerse · Planner mark
 *   • Primary "+ New" CTA → /portal/content-planner (Compose)
 *   • Three grouped nav blocks:
 *       Publish · Library · System
 *   • Switch context block → Tools admin · Admins · public Tools · Site
 *   • Bottom: Buffer connection indicator + sign-out
 *   • Collapsible (rail mode) via toggle; state saved to localStorage
 *   • Theme follows the user's system preference
 *     (prefers-color-scheme: dark / light)
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Plus, PenSquare, CalendarDays, Lightbulb,
  FileText, FileType, Trash2, ChevronLeft, ChevronRight,
  LogOut, ArrowUpRight, ShieldCheck, Users, Wrench, Globe, Palette,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STORAGE_ADMIN = "hv_admin_token";
const STORAGE_COLLAPSED = "hv_planner_sidebar_collapsed";

const GROUPS = [
  {
    label: "Publish",
    items: [
      { key: "compose",  to: "/portal/content-planner",          icon: PenSquare,    label: "Compose" },
      { key: "calendar", to: "/portal/content-planner/calendar", icon: CalendarDays, label: "Calendar" },
    ],
  },
  {
    label: "Library",
    items: [
      { key: "ideas", to: "/portal/workspace/ideas",       icon: Lightbulb, label: "Ideas" },
      { key: "blog",  to: "/portal/content-studio/blog",   icon: FileText,  label: "Blog" },
      { key: "docs",  to: "/portal/content-studio/docs",   icon: FileType,  label: "Documents" },
      { key: "brand", to: "/portal/brand",                 icon: Palette,   label: "Brand kit" },
    ],
  },
  {
    label: "System",
    items: [
      { key: "trash", to: "/portal/workspace/trash", icon: Trash2, label: "Trash" },
    ],
  },
];

// Cross-portal jump targets — accessible from anywhere inside the
// Content Planner so the user never has to sign out + back in to
// reach the rest of the admin or the public tools.
const SWITCH_TARGETS = [
  { key: "access", to: "/portal/access",  icon: ShieldCheck, label: "Tool-access admin" },
  { key: "users",  to: "/portal/admins",  icon: Users,       label: "Admins" },
  { key: "tools",  to: "/tools",          icon: Wrench,      label: "Engineering tools" },
  { key: "site",   to: "/",               icon: Globe,       label: "Marketing site" },
];

function routeMatches(pathname, to) {
  if (to === "/portal/content-planner") {
    // Compose is the exact root only — not calendar/drafts
    return pathname === to;
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

// Hook: returns true when the user's OS prefers dark mode.  Re-renders
// when the user flips the OS setting.
function useSystemDark() {
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return dark;
}

// Theme palette — LIGHT default, DARK only when the OS prefers it.
// Picking values that coordinate with the rest of HidroVerse: cream
// canvas, navy ink, cyan accent.  Dark mode keeps the Buffer-style
// near-black workspace from the previous iteration.
export function plannerTheme(dark) {
  if (dark) {
    return {
      sidebarBg:      "#121212",
      sidebarBorder:  "#262626",
      ink:            "#FAFAFA",
      muted:          "#A3A3A3",
      mutedStrong:    "#737373",
      activeBg:       "#262626",
      hoverBg:        "#1A1A1A",
      accent:         "#1FB6C1",
      brandBg:        "#1FB6C1",
      brandFg:        "#0A0A0A",
      ctaBg:          "#F4F1EB",
      ctaBgHover:     "#FFFFFF",
      ctaFg:          "#0A0A0A",
      panelBg:        "#0F0F0F",
      canvasBg:       "#0A0A0A",
      topBarBg:       "rgba(10,10,10,0.85)",
      topBarText:     "#FAFAFA",
      topBarMuted:    "#737373",
      topBarBorder:   "#262626",
      groupLabel:     "#525252",
    };
  }
  // LIGHT (default)
  return {
    sidebarBg:      "#0C2A40",   // brand navy
    sidebarBorder:  "rgba(255,255,255,0.08)",
    ink:            "#F5F7FA",   // sidebar text on navy
    muted:          "rgba(245,247,250,0.65)",
    mutedStrong:    "rgba(245,247,250,0.45)",
    activeBg:       "rgba(31,182,193,0.18)",
    hoverBg:        "rgba(255,255,255,0.06)",
    accent:         "#1FB6C1",
    brandBg:        "#1FB6C1",
    brandFg:        "#0C2A40",
    ctaBg:          "#1FB6C1",
    ctaBgHover:     "#37C7D1",
    ctaFg:          "#0C2A40",
    panelBg:        "rgba(255,255,255,0.04)",
    canvasBg:       "#FAFAF7",   // cream canvas — matches the marketing site
    topBarBg:       "rgba(250,250,247,0.85)",
    topBarText:     "#0C2A40",
    topBarMuted:    "rgba(12,42,64,0.55)",
    topBarBorder:   "rgba(12,42,64,0.10)",
    groupLabel:     "rgba(245,247,250,0.40)",
  };
}

export default function PlannerSidebar({ mobileOpen = false, onMobileClose, isMobile = false } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const t        = plannerTheme(false); // LIGHT ONLY — dark mode removed.

  const [collapsedPref, setCollapsedPref] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_COLLAPSED) === "1";
  });
  const [bufferOk, setBufferOk] = useState(null); // null=unknown, true/false=known

  // On mobile the drawer is always "expanded" — the collapse toggle is
  // desktop-only and irrelevant when the whole sidebar is off-canvas.
  const collapsed = isMobile ? false : collapsedPref;

  const toggleCollapse = useCallback(() => {
    setCollapsedPref((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_COLLAPSED, next ? "1" : "0"); } catch (e) { /* noop */ }
      return next;
    });
  }, []);

  // Auto-close the mobile drawer on actual route changes so navigation
  // feels native. We track the *previous* pathname via a ref so this
  // effect only fires when the route truly changes — not when the user
  // taps the hamburger and `mobileOpen` flips.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (isMobile && mobileOpen && onMobileClose) onMobileClose();
    }
  }, [location.pathname, isMobile, mobileOpen, onMobileClose]);

  // Probe Buffer status once for the indicator dot. The setState calls
  // are deferred to a microtask via setTimeout so they don't run inside
  // the synchronous effect body — keeps strict React lint rules happy.
  useEffect(() => {
    const token = (typeof window !== "undefined") ? localStorage.getItem(STORAGE_ADMIN) : "";
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      if (cancelled) return;
      if (!token) { setBufferOk(false); return; }
      try {
        const r = await fetch(`${API_BASE}/api/buffer/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        if (!r.ok) { setBufferOk(false); return; }
        const data = await r.json();
        if (cancelled) return;
        setBufferOk(Boolean(data && data.connected));
      } catch (_) {
        if (!cancelled) setBufferOk(false);
      }
    }, 0);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, []);

  const signOut = useCallback(() => {
    try { localStorage.removeItem(STORAGE_ADMIN); } catch (e) { /* noop */ }
    navigate("/admin/login");
  }, [navigate]);

  const width = isMobile ? 280 : (collapsed ? 72 : 260);

  return (
    <>
    {/* Backdrop — only rendered on mobile when the drawer is open. */}
    {isMobile && mobileOpen && (
      <div
        data-testid="planner-sidebar-backdrop"
        onClick={onMobileClose}
        style={{
          position: "fixed", inset: 0, zIndex: 49,
          background: "rgba(12,42,64,0.55)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          animation: "hvFade 180ms ease",
        }}
      />
    )}
    <aside
      data-testid="planner-sidebar"
      style={{
        position: "fixed", left: 0, top: 0, bottom: 0,
        width,
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1), transform 0.28s cubic-bezier(0.4,0,0.2,1)",
        transform: isMobile && !mobileOpen ? "translateX(-100%)" : "translateX(0)",
        background: t.sidebarBg,
        borderRight: `1px solid ${t.sidebarBorder}`,
        display: "flex", flexDirection: "column",
        zIndex: 50,
        color: t.muted,
        fontFamily: "Inter, IBM Plex Sans, system-ui, sans-serif",
        boxShadow: isMobile && mobileOpen ? "0 20px 60px -10px rgba(0,0,0,0.45)" : "none",
      }}
    >
      {/* Brand mark */}
      <div
        style={{
          height: 64, display: "flex", alignItems: "center",
          padding: collapsed ? "0" : "0 20px",
          justifyContent: collapsed ? "center" : "flex-start",
          borderBottom: `1px solid ${t.sidebarBorder}`,
        }}
        data-testid="planner-sidebar-brand"
      >
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: t.brandBg,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, color: t.brandFg, fontSize: 14,
          }}>H</div>
          {!collapsed && (
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ color: t.ink, fontSize: 13, fontWeight: 600, letterSpacing: 0.1 }}>HidroVerse</div>
              <div style={{ color: t.mutedStrong, fontSize: 10, fontFamily: "IBM Plex Mono, ui-monospace, monospace", letterSpacing: "0.18em", textTransform: "uppercase" }}>Planner</div>
            </div>
          )}
        </Link>
      </div>

      {/* + New CTA */}
      <Link
        to="/portal/content-planner"
        data-testid="planner-new-post-btn"
        style={{
          margin: collapsed ? "12px 12px 0" : "16px 16px 0",
          height: 40, borderRadius: 8,
          background: t.ctaBg, color: t.ctaFg,
          display: "flex", alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          padding: collapsed ? 0 : "0 14px",
          gap: 8, fontWeight: 600, fontSize: 13,
          textDecoration: "none",
          boxShadow: "0 6px 18px -6px rgba(31,182,193,0.45)",
          transition: "background-color 0.15s ease, box-shadow 0.2s ease, transform 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = t.ctaBgHover;
          e.currentTarget.style.boxShadow = "0 12px 28px -8px rgba(31,182,193,0.65)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = t.ctaBg;
          e.currentTarget.style.boxShadow = "0 6px 18px -6px rgba(31,182,193,0.45)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <Plus size={16} strokeWidth={2} />
        {!collapsed && <span>New post</span>}
      </Link>

      {/* Nav groups */}
      <nav style={{ flex: 1, marginTop: 20, overflowY: "auto", paddingBottom: 12 }}>
        {GROUPS.map((g) => (
          <div key={g.label} style={{ marginBottom: 18 }}>
            {!collapsed && (
              <div style={{
                padding: "0 22px 8px",
                fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                fontSize: 10, letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: t.groupLabel, fontWeight: 500,
              }}>
                {g.label}
              </div>
            )}
            {g.items.map((it) => {
              const Icon = it.icon;
              const active = routeMatches(location.pathname, it.to);
              return (
                <Link
                  key={it.key}
                  to={it.to}
                  data-testid={`planner-sidebar-${it.key}-link`}
                  style={{
                    display: "flex", alignItems: "center",
                    gap: 12,
                    margin: "1px 8px",
                    padding: collapsed ? "10px 0" : "9px 14px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    borderRadius: 8,
                    color: active ? t.ink : t.muted,
                    background: active ? t.activeBg : "transparent",
                    textDecoration: "none",
                    fontSize: 13, fontWeight: active ? 500 : 400,
                    transition: "background-color 0.12s ease, color 0.12s ease",
                    borderLeft: active ? `2px solid ${t.accent}` : "2px solid transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = t.hoverBg;
                      e.currentTarget.style.color = t.ink;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = t.muted;
                    }
                  }}
                  title={collapsed ? it.label : undefined}
                >
                  <Icon size={18} strokeWidth={1.5} />
                  {!collapsed && <span>{it.label}</span>}
                </Link>
              );
            })}
          </div>
        ))}

        {/* SWITCH CONTEXT — cross-portal jump section so the user can
            reach the rest of the admin (and the public tools) without
            signing out + back in. */}
        <div style={{ marginTop: 6, marginBottom: 12,
                      paddingTop: 14,
                      borderTop: `1px solid ${t.sidebarBorder}` }}>
          {!collapsed && (
            <div style={{
              padding: "0 22px 8px",
              fontFamily: "IBM Plex Mono, ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: t.groupLabel, fontWeight: 500,
            }}>
              Switch
            </div>
          )}
          {SWITCH_TARGETS.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.key}
                to={it.to}
                data-testid={`planner-switch-${it.key}-link`}
                style={{
                  display: "flex", alignItems: "center",
                  gap: 12,
                  margin: "1px 8px",
                  padding: collapsed ? "10px 0" : "9px 14px",
                  justifyContent: collapsed ? "center" : "flex-start",
                  borderRadius: 8,
                  color: t.muted,
                  background: "transparent",
                  textDecoration: "none",
                  fontSize: 13, fontWeight: 400,
                  transition: "background-color 0.12s ease, color 0.12s ease",
                  borderLeft: "2px solid transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = t.hoverBg;
                  e.currentTarget.style.color = t.ink;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = t.muted;
                }}
                title={collapsed ? it.label : undefined}
              >
                <Icon size={18} strokeWidth={1.5} />
                {!collapsed && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {it.label} <ArrowUpRight size={11} strokeWidth={1.8} />
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Buffer connection indicator */}
      <div
        data-testid="buffer-connection-status"
        style={{
          margin: "0 12px 8px",
          padding: collapsed ? "10px 0" : "10px 12px",
          border: `1px solid ${t.sidebarBorder}`,
          borderRadius: 8,
          background: t.panelBg,
          display: "flex", alignItems: "center",
          gap: 10,
          justifyContent: collapsed ? "center" : "flex-start",
          fontSize: 11,
          color: t.muted,
        }}
        title={
          bufferOk === true ? "Buffer · connected"
          : bufferOk === false ? "Buffer · not connected"
          : "Buffer · checking"
        }
      >
        <span style={{
          width: 8, height: 8, borderRadius: "50%",
          background: bufferOk === true ? "#22C55E" : bufferOk === false ? t.mutedStrong : t.muted,
          boxShadow: bufferOk === true ? "0 0 8px rgba(34,197,94,0.55)" : "none",
        }} />
        {!collapsed && (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
            <span style={{ fontFamily: "IBM Plex Mono, ui-monospace, monospace", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: t.mutedStrong }}>
              Buffer
            </span>
            <span style={{ color: bufferOk === true ? t.ink : t.muted, fontSize: 12 }}>
              {bufferOk === true ? "Connected" : bufferOk === false ? "Not connected" : "Checking…"}
            </span>
          </div>
        )}
      </div>

      {/* Footer row: sign out + collapse */}
      <div style={{
        borderTop: `1px solid ${t.sidebarBorder}`,
        padding: collapsed ? "10px 0" : "10px 12px",
        display: "flex", alignItems: "center",
        gap: 8,
        justifyContent: collapsed ? "center" : "flex-end",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            type="button"
            onClick={signOut}
            data-testid="planner-sidebar-signout"
            title="Sign out"
            style={{
              background: "transparent", border: "none",
              color: t.mutedStrong, cursor: "pointer",
              padding: 6, borderRadius: 8,
              display: "flex", alignItems: "center",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = t.ink; e.currentTarget.style.background = t.hoverBg; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = t.mutedStrong; e.currentTarget.style.background = "transparent"; }}
          >
            <LogOut size={15} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={toggleCollapse}
            data-testid="planner-sidebar-toggle"
            title={collapsed ? "Expand" : "Collapse"}
            style={{
              background: "transparent", border: "none",
              color: t.mutedStrong, cursor: "pointer",
              padding: 6, borderRadius: 8,
              display: isMobile ? "none" : "flex", alignItems: "center",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = t.ink; e.currentTarget.style.background = t.hoverBg; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = t.mutedStrong; e.currentTarget.style.background = "transparent"; }}
          >
            {collapsed ? <ChevronRight size={15} strokeWidth={1.5} /> : <ChevronLeft size={15} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
    </aside>
    </>
  );
}

export { STORAGE_COLLAPSED };
